import { useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";

// simple futuristic confirmation modal (no extra deps)
const futuristicConfirm = ({
  title = "Confirm",
  message = "Are you sure?",
  confirmText = "OK",
  cancelText = "Cancel",
}) => {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.style.position = "fixed";
    overlay.style.inset = "0";
    overlay.style.background = "rgba(0,0,0,0.65)";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.zIndex = "9999";

    const dialog = document.createElement("div");
    dialog.style.background = "#b91c1c"; // Tailwind red-700
    dialog.style.border = "1px solid rgba(185, 28, 28, 0.9)";
    dialog.style.borderRadius = "16px";
    dialog.style.padding = "24px";
    dialog.style.maxWidth = "420px";
    dialog.style.width = "90%";
    dialog.style.boxShadow = "0 0 0 1px rgba(185, 28, 28, 0.35), 0 20px 50px rgba(0,0,0,0.55)";
    dialog.style.color = "#f8fafc";
    dialog.style.fontFamily = "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

    const titleEl = document.createElement("h2");
    titleEl.textContent = title;
    titleEl.style.margin = "0 0 12px";
    titleEl.style.fontSize = "1.3rem";

    const messageEl = document.createElement("p");
    messageEl.textContent = message;
    messageEl.style.margin = "0 0 20px";
    messageEl.style.opacity = "0.9";

    const actions = document.createElement("div");
    actions.style.display = "flex";
    actions.style.justifyContent = "flex-end";
    actions.style.gap = "10px";

    const cancelBtn = document.createElement("button");
    cancelBtn.textContent = cancelText;
    cancelBtn.style.padding = "10px 14px";
    cancelBtn.style.borderRadius = "16px";
    cancelBtn.style.border = "1px solid rgba(254, 254, 254, 0.75)";
    cancelBtn.style.background = "rgba(59, 130, 246, 0.85)";
    cancelBtn.style.color = "rgb(255, 255, 255)";
    cancelBtn.style.cursor = "pointer";

    const confirmBtn = document.createElement("button");
    confirmBtn.textContent = confirmText;
    confirmBtn.style.padding = "10px 14px";
    confirmBtn.style.borderRadius = "16px";
    confirmBtn.style.border = "1px solid rgba(255, 255, 255, 0.9)";
    confirmBtn.style.background = "rgba(185, 28, 28, 0.9)";
    confirmBtn.style.color = "#ffffff";
    confirmBtn.style.cursor = "pointer";

    const close = (result) => {
      document.body.removeChild(overlay);
      resolve(result);
    };

    cancelBtn.addEventListener("click", () => close(false));
    confirmBtn.addEventListener("click", () => close(true));

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) close(false);
    });

    actions.appendChild(cancelBtn);
    actions.appendChild(confirmBtn);

    dialog.appendChild(titleEl);
    dialog.appendChild(messageEl);
    dialog.appendChild(actions);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
  });
};

function SSidebar({
  currentUser,
  handleFileUpload,
  handleDeleteAll, // added
  setNewEvent,
  setIsCreateModalOpen,
  setNewWorkEvent,
  setIsCreateWorkEventModalOpen,
  handleSignOut,
}) {
  const navigate = useNavigate();

  const confirmDelete = async (type) => {
    const confirmed = await futuristicConfirm({
      title: `Delete all ${type} events`,
      message: `Are you sure you want to delete ALL ${type} events? This cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (confirmed) {
      handleDeleteAll(type);
    }
  };

  return (
    <aside className="w-64 bg-white shadow-md flex flex-col">
      <div
        className="flex items-center justify-center p-4 border-b cursor-pointer"
        onClick={() => navigate("/dashboard")}
      >
        <img src={logo} alt="Logo" className="h-12" />
      </div>

      <nav className="flex-1 p-4 space-y-2 text-sm font-sans">
        {currentUser?.role === "Scheduler" && (
          <div className="space-y-2">
            {/* Upload Event */}
            <label
              htmlFor="file-upload-event"
              className="cursor-pointer block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Upload Event (CSV)
            </label>
            <input
              id="file-upload-event"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => handleFileUpload(e, "event")}
            />

            <button
              onClick={() => confirmDelete("event")}
              className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Delete All Events
            </button>

            {/* Upload Work */}
            <label
              htmlFor="file-upload-work"
              className="cursor-pointer block px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              Upload Work (CSV)
            </label>
            <input
              id="file-upload-work"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => handleFileUpload(e, "work")}
            />

            <button
              onClick={() => confirmDelete("work")}
              className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Delete All Work
            </button>

            {/* Create Event */}
            <button
              onClick={() => {
                setNewEvent({
                  event_type: "",
                  title: "",
                  date_and_time: "",
                  primary_technician: "",
                  description: "",
                });
                setIsCreateModalOpen(true);
              }}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              Create Event
            </button>

            {/* Create Work Event */}
            <button
              onClick={() => {
                setNewWorkEvent({
                  event_type: "work",
                  description: "",
                  job_number: "",
                  date_and_time: "",
                  primary_technician: "",
                  additional_technicians: "",
                  property: "",
                  street_address: "",
                  city: "",
                  state: "",
                  zipcode: "",
                });
                setIsCreateWorkEventModalOpen(true);
              }}
              className="w-full px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
            >
              Create Work Event
            </button>
          </div>
        )}
      </nav>

      <div className="p-4 border-t font-sans">
        <button
          onClick={handleSignOut}
          className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Sign Out
        </button>
      </div>
    </aside>
  );
}

export default SSidebar;
